/**
 * Support Service
 * API calls for support tickets, feedback, and knowledge base
 */

import { apiClient } from './apiClient';
import {
  SupportTicket,
  TicketStats,
  TicketFilters,
  Feedback,
  FeedbackStats,
  FeedbackFilters,
  KnowledgeBaseArticle,
  KBStats,
  KBFilters,
} from '../types/support';

export const supportService = {
  /**
   * Get support tickets with filtering
   */
  async getTickets(filters?: TicketFilters): Promise<{ tickets: SupportTicket[]; count: number }> {
    const params = new URLSearchParams();
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.priority && filters.priority !== 'all') params.append('priority', filters.priority);
    if (filters?.category && filters.category !== 'all') params.append('category', filters.category);
    if (filters?.assignedTo && filters.assignedTo !== 'all') params.append('assignedTo', filters.assignedTo);
    if (filters?.customerId) params.append('customerId', filters.customerId);
    if (filters?.language && filters.language !== 'all') params.append('language', filters.language);

    const response = await apiClient.get(`/getSupportTickets?${params.toString()}`);
    return response.data;
  },

  /**
   * Get ticket statistics
   */
  async getTicketStats(): Promise<{ stats: TicketStats }> {
    const response = await apiClient.get('/getSupportTicketStats');
    return response.data;
  },

  /**
   * Create new ticket
   */
  async createTicket(ticketData: Partial<SupportTicket>): Promise<{ ticketId: string; ticketNumber: string }> {
    const response = await apiClient.post('/createSupportTicket', ticketData);
    return response.data;
  },

  /**
   * Update ticket
   */
  async updateTicket(ticketId: string, updates: Partial<SupportTicket>): Promise<{ ticketId: string }> {
    const response = await apiClient.put('/updateSupportTicket', { ticketId, updates });
    return response.data;
  },

  /**
   * Get feedback with filtering
   */
  async getFeedback(filters?: FeedbackFilters): Promise<{ feedback: Feedback[]; count: number }> {
    const params = new URLSearchParams();
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.priority && filters.priority !== 'all') params.append('priority', filters.priority);

    const response = await apiClient.get(`/getFeedback?${params.toString()}`);
    return response.data;
  },

  /**
   * Get knowledge base articles
   */
  async getArticles(filters?: KBFilters): Promise<{ articles: KnowledgeBaseArticle[]; count: number }> {
    const params = new URLSearchParams();
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.category && filters.category !== 'all') params.append('category', filters.category);
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.language && filters.language !== 'all') params.append('language', filters.language);

    const response = await apiClient.get(`/getKnowledgeArticles?${params.toString()}`);
    return response.data;
  },
};
